import { Link } from "react-router-dom";
import { ArrowRight, Shield, TrendingUp, Server, Users, BarChart3, Zap, ShieldCheck, Brain } from "lucide-react";

const stats = [
  { value: "100+", label: "ERP Implementations", sublabel: "with measurable ROI" },
  { value: "30%", label: "Cost Savings", sublabel: "on IT operations" },
  { value: "25+", label: "Enterprise Clients Impacted", sublabel: "globally supported" },
  { value: "75+", label: "Years of Collective Expertise", sublabel: "of global expertise" },
];

const services = [
  {
    title: "CIO as a Service",
    desc: "Strategic IT leadership on demand. Executive-level guidance without the cost of a full-time CIO.",
    icon: Users,
    path: "/services/cio",
  },
  {
    title: "IT Infrastructure Management",
    desc: "24x7 monitoring, proactive support, and seamless scalability for hybrid workloads.",
    icon: Server,
    path: "/services/infrastructure",
  },
  {
    title: "ERP Operations & Management",
    desc: "End-to-end ERP solutions that extend workflows into real-world execution.",
    icon: BarChart3,
    path: "/services/erp",
  },
  {
    title: "Cybersecurity & Compliance",
    desc: "Protect your data, secure operations, and ensure audit readiness across regulations.",
    icon: ShieldCheck,
    path: "/services/cybersecurity",
  },
  {
    title: "AI Services",
    desc: "Enterprise grade AI solutions for intelligent decision making and automation.",
    icon: Brain,
    path: "/services/ai",
  },
];

const approachItems = [
  { icon: TrendingUp, title: "Financial Efficiency", desc: "Optimize IT spend, reduce total cost of ownership" },
  { icon: Shield, title: "Risk & Compliance", desc: "Safeguard data, ensure audit readiness" },
  { icon: Zap, title: "Operational Resilience", desc: "Build scalable, secure, future-ready infrastructure" },
  { icon: ArrowRight, title: "Growth Enablement", desc: "Free leadership to focus on expansion" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1644325349124-d1756b79dd42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBkYXRhJTIwbmV0d29ya3xlbnwwfHx8fDE3NzYxMDA4Mzd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-6 animate-fade-in-up opacity-0 stagger-1">
              TEQXL Technology Consultancy
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-6 animate-fade-in-up opacity-0 stagger-2">
              Turning Technology
              <br />
              into Business
              <br />
              <span className="text-[#002FA7]">Value</span>
            </h1>
            <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed max-w-lg mb-10 animate-fade-in-up opacity-0 stagger-3">
              Deep global experience in managing enterprise-scale digital transformation.
              We enable clients to streamline operations with precision, control, and confidence.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link
                to="/services"
                data-testid="hero-explore-services"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
              >
                Explore Services <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-us"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Control Room Grid */}
      <section data-testid="stats-section" className="border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className={`px-6 lg:px-10 py-10 lg:py-14 ${
                  i < 3 ? "border-r border-gray-200" : ""
                } ${i < 2 ? "border-b lg:border-b-0 border-gray-200" : ""}`}
              >
                <p className="font-heading text-3xl lg:text-4xl tracking-tighter font-bold text-[#002FA7] mb-2">
                  {stat.value}
                </p>
                <p className="text-sm font-semibold text-[#0A0A0A] mb-1">{stat.label}</p>
                <p className="text-xs text-[#4B5563]">{stat.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section data-testid="services-preview-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              What We Do
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Enterprise Technology Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200">
            {services.map((service, i) => (
              <Link
                key={service.title}
                to={service.path}
                data-testid={`service-card-${i}`}
                className={`group relative p-8 lg:p-10 border-b lg:border-b border-gray-200 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""} hover:bg-gray-50 transition-colors`}
              >
                <service.icon className="w-8 h-8 text-[#002FA7] mb-6" />
                <h3 className="font-heading text-xl sm:text-2xl tracking-tight text-[#0A0A0A] mb-3 group-hover:text-[#002FA7] transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-6">{service.desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[#002FA7] opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn More <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section data-testid="approach-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                Our Approach
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                We focus on business impact, not just systems
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Every IT decision should strengthen your bottom line, enhance resilience,
                and accelerate business growth. We partner with business leaders to ensure
                technology delivers measurable business outcomes.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-gray-200 bg-white">
              {approachItems.map((item, i) => (
                <div
                  key={item.title}
                  className={`p-6 lg:p-8 ${i % 2 === 0 ? "border-r border-gray-200" : ""} ${
                    i < 2 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <item.icon className="w-5 h-5 text-[#002FA7] mb-4" />
                  <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{item.title}</h4>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Ready to transform your technology landscape?
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            TEQXL is more than a consultancy. We are your strategic technology partner.
            Let every IT decision accelerate your business growth.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Start a Conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
